import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import {
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { thousands } from '../../../../utils/utils';
import { IProduct } from '../../../../interfaces/product';
import { SHeadTableCell } from '../../../../components/styledComponents/Tables';
import { IUser } from '../../../../interfaces/user';
import homeStyles from '../../homeStyles';
import { getProductImage } from '../utils/getProductImage';
import { toAbsoluteUrl } from '../../../../../_base';
import { useFormatMessage } from '../../../../hooks';
import { ProductOffer } from './ProductOffer';

interface IProductsProps {
  data: IProduct[];
  isAuthorized: boolean;
  me: IUser | undefined;
}

const ProductsTableForUsersComponent: React.FC<IProductsProps> = ({
  data,
  isAuthorized,
  me,
}) => {
  const intl = useIntl();
  const fm = useFormatMessage();
  const { classes } = useStyles();
  const homeClasses = homeStyles();
  const [productId, setProductId] = useState<number | null>(null);

  const getMinPriceFromOffers = useCallback((product: IProduct): string => {
    if (!product.prices_values || product.prices_values.length === 0) {
      return thousands(String(product.min_price || '-'));
    }

    const minPrices = product.prices_values
      .map(pv => {
        if (pv.prices && pv.prices.length > 0) {
          return Math.min(...pv.prices.map(p => p.price));
        }
        return pv.vendor?.min_price || null;
      })
      .filter((price): price is number => price !== null);

    if (minPrices.length === 0) {
      return thousands(String(product.min_price || '-'));
    }

    return thousands(String(Math.min(...minPrices)));
  }, []);

  const getTotalStockFromOffers = useCallback((product: IProduct): string => {
    if (!product.prices_values || product.prices_values.length === 0) {
      return thousands(String(product.stock_quantity || '-'));
    }

    const totalStock = product.prices_values.reduce((sum, pv) => {
      return sum + (pv.vendor?.stock_quantity || 0);
    }, 0);

    return totalStock > 0 ? thousands(String(totalStock)) : '-';
  }, []);

  const getTotalOrderFromOffers = useCallback((product: IProduct): string => {
    if (!product.prices_values || product.prices_values.length === 0) {
      return thousands(String(product.order_quantity || '-'));
    }

    const totalOrder = product.prices_values.reduce((sum, pv) => {
      return sum + (pv.vendor?.order_quantity || 0);
    }, 0);

    return totalOrder > 0 ? thousands(String(totalOrder)) : '-';
  }, []);

  return (
    <>
      <Row>
        <Col>
          {!data || !data.length ? (
            <Typography className={classes.empty} component='h5' variant='h5'>
              {intl.formatMessage({ id: 'COMMON.LIST.EMPTY' })}
            </Typography>
          ) : (
            <>
              <Card className={classes.card}>
                <CardContent className={homeClasses.classes.tableContainer}>
                  <Table aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <SHeadTableCell className={classes.cell}>
                          {intl.formatMessage({ id: 'PRODUCT.TABLE.PREVIEW' })}
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.cell}>
                          {intl.formatMessage({ id: 'PRODUCT.TABLE.NAME' })}
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.cell}>
                          {intl.formatMessage({ id: 'PRODUCT.TABLE.CATEGORY.NAME' })}
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.cell}>
                          {intl.formatMessage({ id: 'PRODUCT.MIN.PRICE' })}
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.cell}>
                          Доступно со склада
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.cell}>
                          Доступно под заказ
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.cell}>
                          {intl.formatMessage({ id: 'PRODUCT.TABLE.ACTIONS' })}
                        </SHeadTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.map(item => (
                        <TableRow key={item.id}>
                          <TableCell className={classes.rowElements}>
                            <RouterLink to={`/viewproduct/${item.id}`}>
                              <img
                                src={getProductImage(item)}
                                className={classes.img}
                                alt=''
                                onError={(e: any) => {
                                  e.target.src = toAbsoluteUrl('/images/placeholder.png');
                                }}
                              />
                            </RouterLink>
                          </TableCell>
                          <TableCell className={classes.rowElements}>
                            <RouterLink
                              to={`/viewproduct/${item.id}`}
                              className={classes.productNameLink}
                            >
                              {item.sku || '-'}
                            </RouterLink>
                          </TableCell>
                          <TableCell className={classes.rowElements}>
                            {item.category?.name || '-'}
                          </TableCell>
                          <TableCell className={classes.rowElements}>
                            {getMinPriceFromOffers(item)}
                          </TableCell>
                          <TableCell className={classes.rowElements}>
                            {getTotalStockFromOffers(item)}
                          </TableCell>
                          <TableCell className={classes.rowElements}>
                            {getTotalOrderFromOffers(item)}
                          </TableCell>
                          <TableCell className={classes.rowElements}>
                            <Button
                              variant='contained'
                              className={classes.buyButton}
                              onClick={e => {
                                e.preventDefault();
                                if (item.id) setProductId(item.id);
                              }}
                            >
                              {fm('BUY')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </Col>
      </Row>
      <ProductOffer id={productId} closeModal={() => setProductId(null)} />
    </>
  );
};

const useStyles = makeStyles()(theme => {
  return {
    empty: {
      marginBottom: 20,
    },
    card: {
      marginBottom: theme.spacing(3),
      boxShadow: 'none !important',
    },
    img: {
      maxHeight: 55,
      cursor: 'pointer',
    },
    cell: {
      fontWeight: 'bold',
      fontSize: 14,
    },
    rowElements: {
      fontSize: '14px !important',
    },
    productNameLink: {
      color: '#006997',
      textDecoration: 'none',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    buyButton: {
      textTransform: 'inherit',
      fontSize: 14,
      fontWeight: theme.typography.fontWeightRegular,
    },
  };
});

export default ProductsTableForUsersComponent;
